import React, { ReactElement } from 'react';
import { Component } from "puzzle";
import { FILE_LIST_PROPS, FILE_LIST_STATE } from "./Types";
import {
  extendDataItem,
  mapTreeItem,
  TreeList,
  TreeListColumnProps,
  TreeListCellProps,
  TreeListExpandChangeEvent, TreeListRowProps, TreeListRowClickEvent
} from "@progress/kendo-react-treelist";
import classNames from "classnames";
import { Keys } from "@progress/kendo-react-common";
import _ from "lodash";

class FileList extends Component<FILE_LIST_PROPS, FILE_LIST_STATE> {
  state: FILE_LIST_STATE = {
    data: []
  };
  static defaultProps = {};
  expandField = 'expanded';
  subItemsField = 'subItems';

  moduleDidMount = () => {
    const { files } = this.props;
    this.setState({ data: files });
  }

  /**
   * @inheritDoc
   */
  componentDidUpdate = (prevProps: Readonly<any>, prevState: Readonly<any>, snapshot?: any) => {
    const { files } = this.props;
    if (prevProps.files !== files) {
      this.setState({ data: files });
    }
  }

  /**
   * Tree List cell renderer
   */
  cellRender = (defaultCell: ReactElement<HTMLTableCellElement> | null, props: TreeListCellProps): ReactElement<HTMLTableCellElement> => {
    const { field } = props;
    let cell = defaultCell || <td children={[]} />;
    const children = [...cell.props.children];
    let propsClassName = cell.props.className;
    let className = field === 'validationText' ? `${propsClassName} tp-validation-${props.dataItem.fileUploadStatus.toLowerCase()}` : propsClassName;
    let treeLevel = `${this.translate('SRL-level')} ${props.level.length}`;
    let ariaLabel = this.translate(field || '') + ' ' + children[1];
    return (
      <cell.type
        {...cell.props}
        className={className}
        aria-label={`${treeLevel} ${ariaLabel}`}
      >
        <span>{children}</span>
      </cell.type>
    );
  };

  /**
   * Returns columns array - phaseName columns each time except if dialog is opened via reopen action
   */
  getColumns = (): TreeListColumnProps[] => {
    let columns = [{
      field: 'fileName',
      expandable: true,
      title: this.translate('fileName'),
      width: '500px'
    }, {
      field: 'batchName',
      expandable: false,
      title: this.translate('batch'),
      width: '200px'
    }, {
      field: 'targetLanguageName',
      expandable: false,
      title: this.translate('targetLanguage'),
      width: '200px'
    }, {
      field: 'payload',
      expandable: false,
      title: this.translate('payload'),
      width: '125px'
    }, {
      field: 'validationText',
      expandable: false,
      title: this.translate('validation'),
      className: 'tp-validation-cell',
      width: '450px',
      cell: this.validationCellRenderer
    }];
    if (this.props.activeDialog !== "ReopenDialog") {
      columns.splice(3, 0, {
        field: 'phaseName',
        expandable: false,
        title: this.translate('phase'),
        width: '125px'
      });
    }
    return columns;
  }

  /**
   * Handle expand change, change expand field to true/false
   */
  onExpandChange = (event: TreeListExpandChangeEvent) => {
    const expanded = !event.value;
    const tree = [...this.state.data];
    mapTreeItem(tree, event.level, this.subItemsField, (item: any) =>
      extendDataItem(item, this.subItemsField, {
        [this.expandField]: expanded
      })
    );

    this.setState({
      data: tree
    });
  };

  /**
   * Row Click Event handler
   * Expands current row
   * If job is clicked, then dispatch is called to get jobFiles from server
   */
  onRowClick = (event: TreeListRowClickEvent | { dataItem: any }) => {
    const { data } = this.state;
    const { fileName } = event.dataItem;
    let dataItem = _.find(data, { fileName });
    if (dataItem) {
      dataItem.expanded = !dataItem.expanded
    }
    this.setState({ data });
  };

  /**
   * Custom rendering for rows
   */
  rowRender = (row: ReactElement<HTMLTableRowElement>, props: TreeListRowProps): ReactElement<HTMLTableCellElement> => {
    const { dataItem } = props;
    const isExpanded = dataItem.expanded;
    const className = classNames(
      'k-master-row',
      {
        'tp-row-expanded': isExpanded,
        'tp-row-subItem': dataItem.isSubItem
      }
    );
    return (
      <row.type
        {...row.props}
        aria-expanded={isExpanded ? 'true' : 'false'}
        className={className}
        onKeyUp={this.rowKeyUpHandler.bind(this, dataItem)}
        tabIndex={0}
      >
        {row.props.children}
      </row.type>
    );
  };

  /**
   * On enter and space
   * @param dataItem
   * @param e
   */
  rowKeyUpHandler = (dataItem: any, e: KeyboardEvent) => {
    let { keyCode } = e;
    if ([Keys.enter, Keys.space].includes(keyCode)) {
      this.onRowClick({ dataItem: dataItem });
    }
  };

  /**
   * Cell renderer for validation column
   */
  validationCellRenderer = (cellProps: TreeListCellProps) => {
    let field = cellProps.field;
    let dataItem = cellProps.dataItem;
    let { documentName, fileUploadStatus, validationText } = dataItem;
    let propsClassName = cellProps.className;
    let className = `${propsClassName} tp-validation-${fileUploadStatus.toLowerCase()}`;
    let treeLevel = `${this.translate('SRL-level')} ${cellProps.level.length}`;
    let ariaLabel = this.translate(field || '') + ' ' + documentName;
    return (
      <td
        className={className}
        aria-label={`${treeLevel} ${ariaLabel}`}
      >
        <span className={'tp-validation-msg'}>{validationText}</span>
      </td>
    );
  }

  renderBody() {
    const { data } = this.state;
    return (
      <>
        <TreeList
          cellRender={this.cellRender}
          columns={this.getColumns()}
          data={data}
          rowHeight={40}
          expandField={this.expandField}
          onExpandChange={this.onExpandChange}
          onRowClick={this.onRowClick}
          rowRender={this.rowRender}
          subItemsField={this.subItemsField}
        />
      </>
    )
  }

}

export default FileList;
